import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { getCountriesListSortedByPopularity } from "../../../domains/app/utils/countries";
import { searchLabels } from "../../../domains/app/labelSearch";

// Country selection list used both by the onboarding country step and by the
// CountryFixupPage (SSO users missing a country). Presentational only: it
// renders the selectable rows and delegates the selected value, the search
// query (the input lives in the footer so the list scrolls behind it) and the
// surrounding chrome (title / back button / Continue) to the parent. The parent
// owns the scroll container so the list can scroll behind overlaid header/footer.
export function CountryStep({
  country,
  onCountryChange,
  searchQuery,
}: {
  country: string;
  onCountryChange: (value: string) => void;
  searchQuery: string;
}) {
  const { t } = useTranslation();
  const selectedRowClass =
    "border-ds-border-selected bg-ds-secondary text-ds-primary";
  const { activeLang } = useActiveLang();
  const countryOptions = useMemo(
    () => getCountriesListSortedByPopularity(activeLang),
    [activeLang]
  );

  const filteredCountries = searchLabels({
    items: countryOptions,
    query: searchQuery,
    labelOf: (option) => option.name,
  });

  return (
    <div className="flex w-full flex-col gap-2">
      {filteredCountries.length === 0 ? (
        <p className="pt-8 text-center font-dm-sans text-ui-body text-ds-slate-500">
          {t("No country found")}
        </p>
      ) : (
        filteredCountries.map((option) => {
          const isSelected = country === option.value;
          return (
            <button
              key={option.value}
              type="button"
              className={cn(
                "flex h-12 w-full shrink-0 items-center gap-3 rounded-2xl border px-4 text-left transition-colors",
                isSelected ? selectedRowClass : "border-ds-black/6 bg-white"
              )}
              onClick={() => {
                onCountryChange(option.value);
              }}
            >
              <span className="text-2xl leading-none">{option.flag}</span>
              <span className="font-dm-sans text-ui-body font-medium text-primary-black">
                {option.name}
              </span>
            </button>
          );
        })
      )}
    </div>
  );
}
